import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Input, Typography, Toast, Button } from '@douyinfe/semi-ui';
import { IconMapPin, IconSearch, IconChevronUp, IconChevronDown } from '@douyinfe/semi-icons';
import styles from './index.module.scss';

const MapPicker = ({ onChange = () => { }, initialPosition }) => {
    const mapRef = useRef(null);
    const [searchValue, setSearchValue] = useState('');
    const [position, setPosition] = useState(null);
    const [placeSearch, setPlaceSearch] = useState(null);
    const [isPanelExpanded, setIsPanelExpanded] = useState(false);
    const [hasSearchResults, setHasSearchResults] = useState(false);

    const handleSearch = () => {
        if (!placeSearch || !searchValue || !mapRef.current) return;
        setIsPanelExpanded(true);
        placeSearch.search(searchValue, function (status, result) {
            console.log('搜索结果:', status, result);
            setHasSearchResults(status === 'complete' && result.poiList && result.poiList.pois && result.poiList.pois.length > 0);
        });
    };

    useEffect(() => {
        window._AMapSecurityConfig = {
            securityJsCode: process.env.REACT_APP_AMAP_JSCODE
        };

        const initMap = () => {
            const defaultPosition = [114.049148, 22.672431];
            const map = new AMap.Map('map-container', {
                zoom: 14,
                center: initialPosition || defaultPosition,
            });

            // 添加定位插件
            AMap.plugin(['AMap.Geolocation'], () => {
                const geolocation = new AMap.Geolocation({
                    enableHighAccuracy: true,
                    timeout: 10000,
                    buttonPosition: 'RB',
                    buttonOffset: new AMap.Pixel(10, 20),
                    zoomToAccuracy: true
                });
                map.addControl(geolocation);

                // 如果没有初始位置，则尝试获取当前位置
                console.log('初始位置:', initialPosition);
                if (!initialPosition || (initialPosition[0] == 0 && initialPosition[1] == 0)) {
                    geolocation.getCurrentPosition((status, result) => {
                        if (status === 'complete') {
                            const position = [result.position.lng, result.position.lat];
                            map.setCenter(position);
                            marker.setPosition(position);
                            updateLocation(result.position);
                        } else {
                            console.error('定位失败：', result.message);
                            Toast.warning({
                                content: '无法获取您的位置，请检查浏览器定位权限是否开启。\n您可以：\n1. 点击地址栏左侧的锁定图标\n2. 在权限设置中开启"位置信息"\n3. 刷新页面重试',
                                duration: 8
                            });
                            // 回退到默认位置
                            map.setCenter(defaultPosition);
                            marker.setPosition(defaultPosition);
                            updateLocation({ lng: defaultPosition[0], lat: defaultPosition[1] });
                        }
                    });
                }
            });

            const marker = new AMap.Marker({
                position: map.getCenter(),
                draggable: true
            });

            let geocoder;
            AMap.plugin(['AMap.Geocoder', 'AMap.PlaceSearch'], function () {
                geocoder = new AMap.Geocoder({
                    extensions: 'all'
                });
                const placeSearch = new AMap.PlaceSearch({
                    pageSize: 5,
                    pageIndex: 1,
                    city: '全国',
                    citylimit: false,
                    map: map,
                    autoFitView: true,
                    panel: 'panel'
                });
                placeSearch.on('markerClick', function (e) {
                    const poi = e.data;
                    const lnglat = {
                        lng: poi.location.lng,
                        lat: poi.location.lat
                    };
                    marker.setPosition([lnglat.lng, lnglat.lat]);
                    updateLocation(lnglat);
                });
                setPlaceSearch(placeSearch);
            });

            const updateLocation = (lnglat) => {
                geocoder.getAddress(lnglat, (status, result) => {
                    console.log('逆地理编码结果:', status, result);
                    if (status === 'complete' && result.regeocode) {
                        const address = result.regeocode.formattedAddress;
                        onChange({
                            lat: lnglat.lat.toString(),
                            lng: lnglat.lng.toString(),
                            address: address
                        });
                        console.log('选中位置信息:', {
                            经度: lnglat.lng.toString(),
                            纬度: lnglat.lat.toString(),
                            地址: address
                        });
                    }
                });
                onChange({
                    lat: lnglat.lat.toString(),
                    lng: lnglat.lng.toString(),
                });
                setPosition({ lat: lnglat.lat, lng: lnglat.lng });
            };

            marker.on('dragend', (e) => {
                updateLocation(e.lnglat);
            });

            map.on('click', (e) => {
                marker.setPosition(e.lnglat);
                updateLocation(e.lnglat);
            });

            map.add(marker);
            mapRef.current = map;
        };

        if (window.AMap) {
            initMap();
        } else {
            window.initAMap = initMap;
            const script = document.createElement('script');
            script.src = `https://webapi.amap.com/maps?v=2.0&key=${process.env.REACT_APP_AMAP_KEY}&plugin=AMap.Geocoder,AMap.AutoComplete,AMap.PlaceSearch,AMap.Geolocation&callback=initAMap`;
            document.head.appendChild(script);
        }
    }, []);

    return (
        <div className={styles.mapContainer}>
            <div className={styles.inputGroup}>
                <Input
                    prefix={<IconSearch />}
                    value={searchValue}
                    onChange={setSearchValue}
                    placeholder="搜索地址"
                    onEnterPress={handleSearch}
                />
            </div>
            {position && (
                <div className={styles.coordinateInfo}>
                    <Typography.Text type="secondary">
                        经度：{position.lng.toFixed(6)}，纬度：{position.lat.toFixed(6)}
                    </Typography.Text>
                </div>
            )}
            <div className={styles.searchPanelContainer}>
                <div id="panel" className={`${styles.searchPanel} ${isPanelExpanded ? styles.expanded : ''}`} />
                {hasSearchResults && (
                    <Button
                        theme="light"
                        size="small"
                        className={styles.toggleButton}
                        onClick={() => setIsPanelExpanded(!isPanelExpanded)}
                        icon={isPanelExpanded ? <IconChevronDown /> : <IconChevronUp />}
                    >
                        {isPanelExpanded ? '收起' : '展开'}
                    </Button>
                )}
            </div>
            <div id="map-container" className={styles.mapArea} />
        </div>
    );
};

MapPicker.propTypes = {
    onChange: PropTypes.func.isRequired,
    initialPosition: PropTypes.arrayOf(PropTypes.number)
};

export default MapPicker;